import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BookOpenCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { getRealLedger, setGlobalRiskCap } from "@/lib/api";
import { TradeAttributionBlock } from "@/components/TradeAttributionCard";
import { FatherVerdictBlock } from "@/components/FatherVerdictCard";
import { LatentCapBlock } from "@/components/LatentCapCard";

const fmt = (v) => (v > 0 ? `+${v.toFixed(2)}` : v.toFixed(2));
const cls = (v) => (v > 0 ? "text-emerald-300" : v < 0 ? "text-rose-300" : "text-white/40");

export const RealLedgerCard = () => {
  const [data, setData] = useState(null);
  const [cap, setCap] = useState("");

  const load = useCallback(() => getRealLedger().then(setData).catch(() => {}), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const saveCap = async () => {
    const v = parseFloat(cap);
    if (!v) return;
    try {
      const r = await setGlobalRiskCap({ dailyCapUsd: v });
      setData((d) => ({ ...d, risk: r }));
      setCap("");
      toast.success(`Tetto giornaliero: ${v}$`);
    } catch { toast.error("Salvataggio fallito"); }
  };

  const plats = Object.entries(data?.platforms || {});
  const balances = data?.balances || {};
  const risk = data?.risk;
  return (
    <Card className="border-border/70 bg-card/60" data-testid="real-ledger-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex flex-wrap items-center gap-2">
          <BookOpenCheck className="h-4 w-4 text-amber-300" /> Contabilità Reale
          {risk && (
            <Badge variant="outline"
              className={`text-[10px] border-border ${risk.killActive ? "text-rose-300" : "text-muted-foreground"}`}
              data-testid="ledger-risk-badge">
              {risk.killActive ? "KILL ATTIVO" : `oggi ${fmt(risk.todayPnl)}$ / tetto -${risk.capUsd}$`}
            </Badge>
          )}
        </CardTitle>
        <p data-testid="ledger-cost-basis" className="text-[11px] text-muted-foreground leading-snug">
          Chiusure reali · netto dei costi registrati · valute separate. Riconciliazione exchange incompleta.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {balances.polymarket != null && (
          <div className="flex items-center justify-between rounded-md border border-[hsl(var(--primary))]/25 bg-[hsl(var(--primary))]/[0.06] px-3 py-2"
            data-testid="ledger-polymarket-balance">
            <span className="text-[11px] uppercase tracking-wide text-[hsl(var(--primary))]/80">Polymarket · saldo CLOB live</span>
            <span className="font-mono text-sm text-[hsl(var(--primary))]">{balances.polymarket.toFixed(2)}$ <span className="text-[10px] opacity-60">pUSD</span></span>
          </div>
        )}
        {plats.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="ledger-empty">Nessun trade reale registrato.</p>
        ) : (
          <table className="w-full text-[11px] font-mono" data-testid="ledger-table">
            <thead>
              <tr className="text-white/40 text-left">
                <th className="py-1 pr-2">venue</th>
                <th className="px-1.5 text-right">oggi</th>
                <th className="px-1.5 text-right">7g</th>
                <th className="px-1.5 text-right">30g</th>
                <th className="px-1.5 text-right">totale</th>
                <th className="px-1.5 text-right">n</th>
              </tr>
            </thead>
            <tbody>
              {plats.map(([k, p]) => (
                <tr key={k} className="border-t border-border/40" data-testid={`ledger-row-${k}`}>
                  <td data-testid={`ledger-currency-${k}`} className="py-1 pr-2 text-white/70 break-all">{p.venue || k}<span className="block text-[9px] text-white/40">{p.currency}</span></td>
                  <td className={`px-1.5 text-right ${cls(p.today)}`}>{fmt(p.today)}</td>
                  <td className={`px-1.5 text-right ${cls(p.d7)}`}>{fmt(p.d7)}</td>
                  <td className={`px-1.5 text-right ${cls(p.d30)}`}>{fmt(p.d30)}</td>
                  <td className={`px-1.5 text-right ${cls(p.allTime)}`}>{fmt(p.allTime)}</td>
                  <td className="px-1.5 text-right text-white/45">{p.n}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {data?.pmDiag && !data?.platforms?.polymarket && (
          <p className="text-[10px] text-muted-foreground leading-snug" data-testid="ledger-pm-diag">
            polymarket: nessun trade reale <b>chiuso</b> (la tabella mostra solo PnL realizzato)
            {(data.pmDiag.whaleRealOpen > 0 || data.pmDiag.sniperRealOpen > 0) ? (
              <span className="text-amber-300/90">
                {" "}— <b>{data.pmDiag.whaleRealOpen + data.pmDiag.sniperRealOpen} posizioni reali APERTE</b>
                {data.pmDiag.openStakeUsd > 0 && ` (${data.pmDiag.openStakeUsd}$ a rischio)`} → dettaglio e P&L latente in Polymarket → Live
              </span>
            ) : (
              <span> — sniper reale {data.pmDiag.sniperRealOpen} aperti · {data.pmDiag.sniperRealResolved} risolti · copy reali aperte {data.pmDiag.whaleRealOpen}</span>
            )}
          </p>
        )}
        {(data?.accounting?.excluded?.length > 0 || Object.keys(data?.accounting?.warnings || {}).length > 0) && (
          <p data-testid="ledger-accounting-warning" className="text-[10px] text-amber-300/80">Copertura costi/valute parziale · {data.accounting.excluded.length} righe escluse per qualità o duplicazione.</p>
        )}
        <TradeAttributionBlock data={data?.attribution} onDone={load} />
        <LatentCapBlock />
        <FatherVerdictBlock />
        <div className="flex items-center gap-1.5">
          <ShieldAlert className="h-3.5 w-3.5 text-rose-300/70" />
          <Input value={cap} onChange={(e) => setCap(e.target.value)} type="number"
            placeholder={`tetto perdita/giorno (ora ${risk?.capUsd ?? 50}$)`}
            className="h-7 text-[11px] max-w-[220px]" data-testid="ledger-cap-input" />
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={saveCap}
            disabled={!cap} data-testid="ledger-cap-save">Imposta</Button>
        </div>
      </CardContent>
    </Card>
  );
};
